import MnpDataModule from './mnpData'
import CustomerDataModule from './customerData'
import SimDataModule from './simData'
import IdentDataModule from './identData'
import PaymentModule from './paymentData'
import ActivationCartModule from './cart/activationCart.js'
import { getProvider } from '@/apollo'
import mutationActivationInfo from '@/store/modules/checkoutv2/cart/gql/mutationActivationInfo'
import queryPreactivePromotions from '@/store/modules/checkoutv2/cart/gql/queryPreactivePromotions'
import gql from 'graphql-tag'

const initialState = {
  step: 'mnpData',
  confirmResult: {
    contractId: '',
    customerId: ''
  },
  processId: null,
  contractDocumentUrl: null,
  consents: null,
  activationInfo: null,
  params: {
    msisdn: '',
    activationCode: '',
    pPlusToken: null
  }
}

const ActivationModule = {
  namespaced: true,
  modules: {
    _mnpData: MnpDataModule,
    _customerData: CustomerDataModule,
    _simData: SimDataModule,
    _identData: IdentDataModule,
    _paymentData: PaymentModule,
    _cart: ActivationCartModule
  },
  state: initialState,
  getters: {
    step: state => {
      return state.step
    },
    processId: state => {
      return state.processId
    },
    params: state => {
      return state.params
    },
    activationCode: state => {
      return state.params.activationCode
    },
    msisdn: state => {
      return state.params.msisdn
    },
    consents: state => {
      return state.consents
    },
    confirmResult: state => {
      return state.confirmResult
    },
    activationInfo: state => {
      return state.activationInfo
    },
    isWebIdentPending: state => {
      return state.activationInfo?.details?.webIdentPending
    },
    retailerId: state => {
      return state.activationInfo?.details?.retailerId
    },
    contractDocumentUrl: state => {
      return state.contractDocumentUrl
    }
  },
  mutations: {
    setStep (state, step) {
      state.step = step
    },
    setProcessId (state, processId) {
      state.processId = processId
    },
    setContractDocumentUrl (state, contractDocumentUrl) {
      state.contractDocumentUrl = contractDocumentUrl
    },
    setActivationInfo (state, activationInfo) {
      state.activationInfo = activationInfo
    },
    setConsents (state, consents) {
      state.consents = consents
    },
    setConfirmResult (state, confirmResult) {
      state.confirmResult = confirmResult
    },
    setParams (state, params) {
      state.params = {
        ...state.params,
        ...params
      }
    },
    resetState (state) {
      Object.assign(state, initialState)
    }
  },
  actions: {
    resetState  ({ state, commit, dispatch }) {
      commit('_mnpData/resetState', { root: true })
      commit('_customerData/resetState', { root: true })
      commit('_identData/resetState', { root: true })
      commit('_paymentData/resetState', { root: true })
      commit('_cart/resetState', { root: true })
      commit('resetState')
      return true
    },
    async loadActivationInfo ({ commit, state, rootGetters, getters }, { msisdn, activationCode, nextStep = 'personalDataInput', allowNextStep }) {
      commit('setParams', { msisdn, activationCode })
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: mutationActivationInfo,
        variables: {
          activationInfoInput: {
            ...getters.params,
            brand: rootGetters.brand
          }
        }
      }).then((response) => {
        if (response.data.activationInfo) {
          commit('setActivationInfo', response.data.activationInfo)
        } else {
          throw new Error('Activation check failed')
        }
      }).catch((error) => {
        commit('setParams', {
          msisdn: '',
          activationCode: '',
          pPlusToken: null
        })
        throw error
      })
    },
    async loadPreactivePromotions ({ dispatch, commit, state, getters, rootGetters }) {
      const apolloClient = getProvider().defaultClient
      return apolloClient.query({
        query: queryPreactivePromotions,
        variables: {
          msisdn: getters.params.msisdn,
          brand: rootGetters.brand
        }
      }).then((response) => {
        if (response.data.availablePreactivePromotions) {
          // speichern fuer Berechnung des activation carts in promotion/getCartForSelection
          commit('promotion/savePresaleInclusive', { prefetchData: response.data.availablePreactivePromotions.inclusivePromotion, force: true }, { root: true })
          commit('promotion/savePresaleBookable', { prefetchData: response.data.availablePreactivePromotions.bookablePromotion, force: true }, { root: true })
          commit('promotion/SET_PREACTIVE_PROMOTION', response.data.availablePreactivePromotions, { root: true })
        } else {
          throw new Error('Activation check failed')
        }
      }).catch((error) => {
        throw error
      })
    },
    async initiate ({ commit, state, getters, rootGetters }) {
      const targetTariff = getters['_cart/targetTariff']
      const targetTariffoptions = getters['_cart/targetTariffoptions']
      const customerDataPerson = getters['_customerData/person']
      const customerDataAddress = getters['_customerData/address']
      const mnpActive = getters['_mnpData/active']
      const mnpProvider = getters['_mnpData/provider']
      const mnpPortingData = getters['_mnpData/portingData']
      const mnpContractHolder = getters['_mnpData/contractHolder']
      const bankAccount = getters['_paymentData/bankAccount']
      const simData = getters['_simData/simData']
      const identData = getters['_identData/identData']
      const voucherCode = getters['_cart/voucherCode']
      let tariffoptions = null
      if (targetTariffoptions && Array.isArray(targetTariffoptions) && targetTariffoptions.length === 1) {
        tariffoptions = {
          TariffoptionInput: {
            tariffoptionId: targetTariffoptions[0].tariffoptionId
          }
        }
      }
      const inputVariables = {
        customer: {
          address: customerDataAddress,
          gender: customerDataPerson.gender,
          firstName: customerDataPerson.firstName,
          lastName: customerDataPerson.lastName,
          birthDate: customerDataPerson.birthDate,
          email: customerDataPerson.email,
          contactNumber: customerDataPerson.phone
        },
        brand: rootGetters.brand,
        payment: {
          owner: bankAccount.accountHolder,
          iban: bankAccount.iban,
          paymentType: 'bankAccount'
        },
        deferredIdentification: {
          identMethod: identData.identType,
          nationality: identData.nationality.iso3,
          medium: identData.identCard.key
        },
        msisdn: getters.params.msisdn,
        preactiveCardAuthentication: {
          activationCode: getters.params.activationCode,
          pPlusToken: getters.params.pPlusToken
        },
        product: {
          tariffIdentifier: targetTariff.tariffId,
          promotions: null,
          tariffoptions: tariffoptions,
          article: null,
          promoCode: voucherCode,
          simType: simData.simType
        },
        orderContext: {
          market: 'PUC',
          retailerId: getters.retailerId
        }
      }
      if (mnpActive) {
        inputVariables.mobileNumberPortability = {
          networkKey: mnpProvider.network,
          portingDateType: mnpPortingData.dateType,
          portingMsisdn: mnpPortingData.msisdn,
          providerKey: mnpProvider.key
        }
        if (mnpContractHolder.firstName) {
          inputVariables.mobileNumberPortability = {
            ...inputVariables.mobileNumberPortability,
            firstName: mnpContractHolder.firstName,
            lastName: mnpContractHolder.lastName,
            birthDate: mnpContractHolder.birthDate
          }
        }
      }
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`
          mutation activation ($activationInput: ActivationInput!) {
            activationInitiate (activationInput: $activationInput){
              contractDocumentUrl
              processId
            }
          }`,
        variables: {
          activationInput: inputVariables
        }
      }).then((response) => {
        if (response.data.activationInitiate) {
          commit('setProcessId', response.data.activationInitiate.processId)
          commit('setContractDocumentUrl', response.data.activationInitiate.contractDocumentUrl + '?contentDisposition=inline')
        } else {
          throw new Error('Initiate failed')
        }
      }).catch((error) => {
        throw error
      })
    },
    async confirm ({ commit, state, rootState, rootGetters, getters, dispatch }, consents) {
      const inputVariables = {
        brand: rootGetters.brand,
        msisdn: getters.params.msisdn,
        processId: getters.processId,
        consent: {
          adConsent: consents.contact,
          passData: consents.exchange,
          agb: consents.agb,
          contractTermsVersion: consents.contractTermsVersion
        }
      }
      const apolloClient = getProvider().defaultClient
      return apolloClient.mutate({
        mutation: gql`
          mutation activation ($activationConfirmInput: ActivationConfirmInput!) {
            activationConfirm (activationConfirm: $activationConfirmInput){
              contractId,
              customerId,
              deferredIdentificationResponse {
                actionID,
                identProvider,
                url
              }
            }
          }`,
        variables: {
          activationConfirmInput: inputVariables
        }
      }).then((response) => {
        commit('setConsents', consents)
        if (response.data.activationConfirm) {
          commit('setConfirmResult', response.data.activationConfirm)
          commit('Transaction/removeTransactionId', 'activation', { root: true })
        } else {
          throw new Error('Confirm failed')
        }
      }).catch((error) => {
        throw error
      })
    }
  }
}

export default ActivationModule
